import React, { lazy, useEffect, useState } from 'react';
import { useVote } from '../../hooks/useVote';
import { HackathonPayload } from '../../services/hackathon.service';
import { useAuth } from '../../services/useAuth';
import { useDesktopMode } from '../../services/hooks/useDesktopMode';
import Card from '@mui/material/Card/Card';
import CardContent from '@mui/material/CardContent/CardContent';
import Typography from '@mui/material/Typography/Typography';
import Stack from '@mui/material/Stack/Stack';
import ToggleButton from '@mui/material/ToggleButton/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup/ToggleButtonGroup';
import TextField from '@mui/material/TextField/TextField';
import Button from '@mui/material/Button/Button';
import Alert from '@mui/material/Alert/Alert';
import CircularProgress from '@mui/material/CircularProgress/CircularProgress';
import { IdeaPayload } from '../../services/idea.service';
import { submitVote } from '../../services/vote.service';
import { SessionPayload } from '../../services/session.service';

// a score of 0 is stored as abstaining; it has its own button so it can't be mistaken for part of the scale
const scoreValues = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export const JudgeVoteCard = ({
    hackathon,
    idea,
    session,
    onSubmit,
    onDirtyChange
}: {
    hackathon: HackathonPayload,
    idea: IdeaPayload,
    // with sessions: the session this vote is for; only its categories are scored
    session?: SessionPayload,
    onSubmit?: () => void,
    // told when picks are made but not yet submitted, and when they're saved
    onDirtyChange?: (dirty: boolean) => void
}) => {

    let { vote, reload } = useVote(hackathon._id, idea._id, session?._id)
    const { user } = useAuth();

    // in a session only its own judges count as judges
    const isJudge = !!user && (session
        ? session.judges.some(j => j.email === user.email)
        : hackathon.judges.some(j => j.email === user.email));
    const sessionCategories = session ? new Set(session.categories) : null;

    const [notes, setNotes] = useState<string[]>([]);
    const [feedback, setFeedback] = useState('');
    const [scores, setScores] = useState<number[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [showSubmitMessage, setShowSubmitMessage] = useState(false);
    // picked but not submitted: the "8 / 10" readout looks the same either way, so say it isn't saved yet
    const [dirty, setDirty] = useState(false);
    const [submitError, setSubmitError] = useState('');
    // categories the judge explicitly abstained on (a 0 score that was chosen, not just left unscored)
    const [abstained, setAbstained] = useState<boolean[]>([]);
    // unscored categories are almost always an oversight; name them once before saving
    const [confirmUnscored, setConfirmUnscored] = useState(false);

    const [savedMessage, setSavedMessage] = useState('');
    const [hasSavedVote, setHasSavedVote] = useState(false);

    const desktopMode = useDesktopMode();
    useEffect(() => {
        onDirtyChange && onDirtyChange(dirty);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dirty]);
    useEffect(() => {
        if (vote) {
            const saved = !!vote._id && vote._id !== '0' && (vote.scores || []).some(s => s > 0);
            setHasSavedVote(saved);
            // a saved 0 was a choice the judge already made, unless the category was added after they voted
            const answered = vote.answered;
            setAbstained((vote.scores || []).map((s, i) => saved && !s && (answered ? !!answered[i] : true)));
            setNotes(vote.notes || []);
            setFeedback(vote.feedback || '');
            setDirty(false);
            setScores(vote.scores || []);
        }
    }, [vote])

    if (!hackathon || !hackathon.categories || hackathon.categories.length === 0) {
        return <Card>
            <CardContent>
                <Typography variant="h6">No categories to vote on</Typography>
            </CardContent>
        </Card>
    }

    if (!vote || !vote.scores) {
        if (!vote) {
            vote = {
                _id: '0',
                userId: '0',
                ideaId: idea._id,
                scores: new Array(hackathon.categories.length).fill(0),
                notes: new Array(hackathon.categories.length).fill(''),
            }
        }
        vote.scores = new Array(hackathon.categories.length).fill(0);
        vote.notes = new Array(hackathon.categories.length).fill('');
    }

    const unscoredCategories = hackathon.categories.filter((_, index) => {
        const meta = hackathon.categoryMeta[index];
        const inSession = !sessionCategories || (!!meta?._id && sessionCategories.has(meta._id));
        const visible = inSession && (meta?.judgeOnly ? isJudge : true);
        return visible && !vote.scores[index] && !abstained[index];
    });


    return (
        <Card>
            <CardContent sx={{ overflow: 'auto' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mx: 2 }}>
                    Score each category from <b>1</b> (weak) to <b>10</b> (excellent), or choose <b>Abstain</b> to skip it.
                </Typography>
                {
                    // categories added (or opened to this voter) after they voted: say so, rather than let them pass for abstains
                    hasSavedVote && unscoredCategories.length > 0 && <Alert severity="warning" sx={{ mx: 2, mt: 1 }}>
                        Your vote doesn't cover <b>{unscoredCategories.join(', ')}</b> yet (added after you voted). Score {unscoredCategories.length === 1 ? 'it' : 'them'} and click <b>Update vote</b>.
                    </Alert>
                }
                <Stack direction="row" flexWrap={"wrap"} spacing={0}>
                    {
                        (<>
                            {
                                hackathon.categories.map((category, index) => {
                                    const meta = hackathon.categoryMeta[index];
                                    const inSession = !sessionCategories || (!!meta?._id && sessionCategories.has(meta._id));
                                    const visible = inSession && (meta?.judgeOnly ? isJudge : true);
                                    if (inSession && !visible) {
                                        // in its place, so a missing category reads as "not yours" rather than a bug (run026)
                                        return (
                                            <Stack key={category} direction="column" margin={2} width={desktopMode ? 320 : "100%"} spacing={0.5}>
                                                <Typography color="text.secondary"><b>{category}</b>: scored by the judges</Typography>
                                                <Typography variant="body2" color="text.secondary">Only judges score this one, so there's nothing for you to pick.</Typography>
                                            </Stack>
                                        );
                                    }
                                    if (!visible) return null;
                                    return (
                                        <Stack key={category} direction="column" margin={2} width={desktopMode ? 320 : "100%"} spacing={0}>
                                            <Typography sx={{ minWidth: 100 }} id={`score-${idea._id}-${index}`}><b>{category}</b>: {vote.scores[index] ? `${vote.scores[index]} / 10` : abstained[index] ? 'Abstain' : 'not scored yet'}</Typography>
                                            {meta?.description && <Typography variant="body2" color="text.secondary">{meta.description}</Typography>}
                                            <ToggleButtonGroup
                                                exclusive
                                                size="small"
                                                fullWidth
                                                aria-labelledby={`score-${idea._id}-${index}`}
                                                value={vote.scores[index] ? vote.scores[index] : abstained[index] ? 0 : null}
                                                onChange={(e, val) => {
                                                    if (val === null) return;
                                                    vote.scores[index] = val as number;
                                                    abstained[index] = val === 0;
                                                    setAbstained([...abstained]);
                                                    setScores([...vote.scores]);
                                                    setConfirmUnscored(false);
                                                    setShowSubmitMessage(false);
                                                    setDirty(true);
                                                }}
                                                sx={{
                                                    mt: 1, mb: 1,
                                                    // phones: two rows of five, big enough to tap
                                                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                                                    '& .MuiToggleButton-root': { px: 0, minWidth: 0, flex: { xs: '1 0 20%', sm: '1 1 0' }, minHeight: { xs: 44, sm: 40 } },
                                                    '& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover': {
                                                        bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 'bold'
                                                    }
                                                }}
                                            >
                                                {scoreValues.map(score => (
                                                    // the row's heading can scroll out of view on a phone; each button says which category it scores
                                                    <ToggleButton key={score} value={score} aria-label={`${category}: ${score} out of 10`}>{score}</ToggleButton>
                                                ))}
                                            </ToggleButtonGroup>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                                <Typography variant="caption" color="text.secondary">1 = weak · 10 = excellent</Typography>
                                                <Button size="small" aria-label={`Abstain on ${category}`}
                                                    variant={!vote.scores[index] && abstained[index] ? 'contained' : 'text'}
                                                    onClick={() => {
                                                        vote.scores[index] = 0;
                                                        abstained[index] = true;
                                                        setAbstained([...abstained]);
                                                        setScores([...vote.scores]);
                                                        setConfirmUnscored(false);
                                                        setShowSubmitMessage(false);
                                                    setDirty(true);
                                                    }}>Abstain</Button>
                                            </Stack>
                                            <TextField
                                                label={<Typography>Notes (for yourself only)</Typography>}
                                                multiline={true}
                                                minRows={2}
                                                value={notes[index] || ''} onChange={(e) => {
                                                    notes[index] = e.target.value;
                                                    setNotes([...notes]);
                                                    setDirty(true);
                                                }} />
                                        </Stack>
                                    )
                                })
                            }
                        </>)
                    }
                </Stack>

                {
                    // notes stay private; this one is for the team
                    isJudge && <TextField
                        sx={{ mx: 2, mb: 2, width: desktopMode ? 'calc(100% - 32px)' : undefined }}
                        fullWidth={!desktopMode}
                        label="Feedback for the team"
                        helperText="Optional. The team sees this, without your name, once the results are announced."
                        multiline={true}
                        minRows={2}
                        inputProps={{ maxLength: 1000 }}
                        value={feedback}
                        onChange={(e) => {
                            setFeedback(e.target.value);
                            setShowSubmitMessage(false);
                                                    setDirty(true);
                        }} />
                }
                {
                    confirmUnscored && <Alert severity="warning" sx={{ mb: 2 }}>
                        Not scored yet: <b>{unscoredCategories.join(', ')}</b>. Pick a score from 1 to 10,
                        or click <b>Save anyway</b> to abstain on {unscoredCategories.length === 1 ? 'it' : 'them'}.
                    </Alert>
                }
                <Stack direction="row" justifyContent="flex-end" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Button
                        disabled={submitting}
                        variant="contained"
                        onClick={() => {
                            if (!confirmUnscored && unscoredCategories.length > 0) {
                                setConfirmUnscored(true);
                                return;
                            }
                            setConfirmUnscored(false);
                            const updating = hasSavedVote;
                            setSubmitting(true);
                            setSubmitError('');
                            const categoryIds = hackathon.categoryMeta.map(m => m._id);
                            submitVote(hackathon._id, idea._id, vote.scores, notes,
                                categoryIds.every(Boolean) ? categoryIds as string[] : undefined, session?._id, isJudge ? feedback : undefined).then(() => {
                                setSubmitting(false);
                                const time = new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
                                setSavedMessage(`${updating ? 'Vote updated' : 'Vote saved'} at ${time}`);
                                setHasSavedVote(true);
                                // stays until the next change, so it can't be missed
                                setShowSubmitMessage(true);
                                setDirty(false);
                                onSubmit && onSubmit();
                            }, (err) => {
                                console.error(err);
                                setSubmitting(false);
                                setSubmitError(err.response?.data?.message || 'Your vote could not be saved. Please try again.');
                            })
                        }}>{confirmUnscored ? 'Save anyway' : hasSavedVote ? 'Update vote' : 'Submit vote'}</Button>
                    {
                        dirty && !submitting && <Typography variant="body2" color="warning.main" role="status">
                            {hasSavedVote ? 'Changes not saved yet: click Update vote' : 'Not submitted yet'}
                        </Typography>
                    }
                    {
                        submitError && <Alert severity="error">{submitError}</Alert>
                    }
                    {
                        showSubmitMessage && <Alert severity="success">{savedMessage}</Alert>
                    }
                    {
                        submitting && <CircularProgress />
                    }
                </Stack>
            </CardContent>
        </Card>
    )
}